<template>
  <BlokkliProvider v-bind="blokkliProps" :entity="props">
    <ClientOnly>
      <div>
        <MediaImage
          v-if="heroImage"
          v-bind="heroImage"
          :image-style="imageStyle"
          img-class="max-h-[75vh] object-cover"
          class="page-home-hero-image"
        />

        <div
          class="page-home-hero-content container flex-col items-center md:flex"
        >
          <h1
            class="text-4xl font-semibold tracking-wide text-white mobile-only:px-[18px] mobile-only:leading-tight md:text-7xl"
          >
            {{ title }}
          </h1>

          <form
            id="search-home-form"
            class="mt-24 w-full rounded-[30px] bg-white px-20 py-24 shadow-xl md:max-w-screen-xs md:p-40"
            @submit.prevent="search"
          >
            <p class="mb-24 text-2xl font-semibold tracking-wide md:text-4xl">
              {{ $texts('whatAreYouSearching') }}
            </p>
            <SearchInput id="search-home" v-model="searchTerm" />
          </form>

          <p
            v-if="intro"
            v-blokkli-editable:field_intro
            class="typo-lead-1 text-black mt-20 text-pretty md:mt-56"
            v-html="intro"
          />
        </div>
      </div>

      <QuickLinks
        v-if="myPublicaLink?.url?.path"
        :to="myPublicaLink.url?.path"
        :title="
          $texts('homepageLinkMessage')
        "
        linktext="myPublica"
        :page="'home'"
      />
    </ClientOnly>

    <BlokkliField
      :list="paragraphs"
      name="field_paragraphs"
      class="page-home-teasers"
    />
  </BlokkliProvider>
</template>

<script setup lang="ts">
import type { NodePageFragment } from '#graphql-operations'

const props = defineProps<{
  title?: string
  intro?: string
  leadTitle?: string
  pageColor?: string
  hasBackground?: boolean
  paragraphs?: NodePageFragment['paragraphs']
  body?: string
  blokkliProps: NodePageFragment['blokkliProps']
}>()

const searchTerm = ref('')

const router = useRouter()
const initData = await useInitData()
const { dataLayerPush } = useAnalytics()
const { isMobile } = useViewport()

const { data: heroImage } = await useAsyncGraphqlQuery(
  'homepageHeroimage',
  null,
  {
    transform: function (data) {
      return data.data.config?.image
    },
    graphqlCaching: {
      client: true,
    },
  },
)

const myPublicaLink = computed(() => {
  return initData.value.siteConfig.pageMypublica
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: isMobile.value ? 3 / 5 : 16 / 9,
  sizes: {
    xs: 770,
    sm: 984,
    md: 1380,
    lg: 1380,
  },
})

const search = () => {
  dataLayerPush({
    event: 'site_search',
    search_term: searchTerm.value,
  })
  router.push({
    name: 'search',
    query: {
      text: searchTerm.value,
    },
  })
}

useHead({
  htmlAttrs: {
    class: 'has-highlighted-hero',
  },
})
</script>

<style lang="postcss">
.page-home-hero-image {
  transform: translateY(-80px);

  @screen md {
    transform: translateY(-202px);
  }
}

.page-home-hero-content {
  transform: translateY(-185px);
  margin-top: -80px;
  margin-bottom: -160px;
  @screen md {
    transform: translateY(-310px);
  }
}

.page-home-hero-search {
  @screen sm {
    max-width: 35rem;
    margin: 0 auto;
    margin-top: -3rem;
  }
  @screen md {
    margin-top: -6rem;
  }
  @screen lg {
    margin-top: -10rem;
  }
}
.page-home-teasers {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 2rem;
  grid-gap: calc(var(--grid-gap) * 2);
}

#search-home-form {
  label {
    @apply hidden;
  }

  #search-home {
    @apply rounded-large border-gray-300 bg-gray-50;
  }

  button {
    @apply rounded-full border-none bg-blue-700 p-12;

    svg {
      @apply m-0 h-28 w-28;
    }
    span {
      /* Keep this button icon-only at every breakpoint without removing its
         accessible name (md:hidden would set display:none, which strips it
         from the accessibility tree entirely). */
      @apply md:sr-only;
    }
  }
}
</style>
